/**
 * ConfirmDialog — editorial replacement for window.confirm().
 *
 * Two ways to use it:
 *
 *   1. Imperative (most common — no JSX boilerplate at the call site):
 *        import { confirm } from "../components/ConfirmDialog";
 *        const ok = await confirm({
 *          title: "Delete session",
 *          description: "This permanently removes the chat history.",
 *          confirmLabel: "Delete",
 *          tone: "danger",            // optional — paints the confirm button terracotta
 *        });
 *        if (!ok) return;
 *
 *   2. Declarative (when you need full control):
 *        <ConfirmDialog open={open} onOpenChange={setOpen} title="..." onConfirm={...} />
 *
 * The imperative API mounts a single host portal at app start (see <ConfirmHost />
 * in App.jsx) and works by pushing a request onto a Zustand-style channel.
 *
 * Why Radix Dialog as the underlying primitive:
 *   - Focus trap, ESC dismissal, and body scroll lock for free.
 *   - Accessible by default (role=dialog, aria-modal, labelled by title).
 *   - Headless — we own 100% of the visual design.
 */
import { useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { create } from "zustand";

// ─── Imperative channel ──────────────────────────────────────────────────────

const useConfirmStore = create((set) => ({
  request: null,                  // { resolve, options } when a confirm is pending
  open: (options) =>
    new Promise((resolve) => {
      set({ request: { options, resolve } });
    }),
  close: (result) => {
    const req = useConfirmStore.getState().request;
    if (req) {
      req.resolve(result);
      set({ request: null });
    }
  },
}));

/**
 * Imperative entry point — call from anywhere.
 * Resolves to true (confirmed) or false (cancelled / dismissed).
 */
export function confirm(options) {
  return useConfirmStore.getState().open(options);
}

// ─── Host portal — mounted once at the app root ──────────────────────────────

export function ConfirmHost() {
  const request = useConfirmStore((s) => s.request);
  const close   = useConfirmStore((s) => s.close);

  // Lock close-via-ESC & overlay click to "cancelled" (false) — never undefined.
  function onOpenChange(nextOpen) {
    if (!nextOpen) close(false);
  }

  if (!request) return null;
  const o = request.options;

  return (
    <ConfirmDialog
      open
      onOpenChange={onOpenChange}
      title={o.title}
      description={o.description}
      confirmLabel={o.confirmLabel || "Confirm"}
      cancelLabel={o.cancelLabel || "Cancel"}
      tone={o.tone}
      onConfirm={() => close(true)}
    />
  );
}

// ─── Declarative component (also used internally by ConfirmHost) ─────────────

export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "neutral",          // "neutral" | "danger"
  onConfirm,
}) {
  // Auto-focus the confirm button when the dialog opens for keyboard flow.
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      const btn = document.querySelector("[data-confirm-primary]");
      btn?.focus();
    }, 50);
    return () => clearTimeout(id);
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="
            fixed inset-0 z-40
            bg-[var(--color-ink)]/30
            data-[state=open]:animate-[fadeIn_180ms_ease-out]
          "
        />
        <Dialog.Content
          className="
            fixed left-1/2 top-1/2 z-50
            -translate-x-1/2 -translate-y-1/2
            w-[min(28rem,calc(100vw-2rem))]
            bg-[var(--color-paper)]
            border border-[var(--color-rule-strong)]
            data-[state=open]:animate-[scaleIn_240ms_cubic-bezier(0.22,1,0.36,1)]
            outline-none
          "
        >
          <div className="px-7 pt-7 pb-5">
            <div className="eyebrow mb-4">
              {tone === "danger" ? "DESTRUCTIVE ACTION" : "CONFIRM"}
            </div>
            <Dialog.Title className="display text-[var(--text-2xl)] leading-[1.1]">
              {title}
            </Dialog.Title>
            {description && (
              <Dialog.Description className="mt-3 text-[var(--text-base)] text-[var(--color-ink-2)] leading-[1.55]">
                {description}
              </Dialog.Description>
            )}
          </div>

          <div className="px-7 py-4 border-t border-[var(--color-rule)] flex items-center justify-end gap-3">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => onOpenChange(false)}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              data-confirm-primary
              className={tone === "danger" ? "btn btn-accent" : "btn btn-primary"}
              onClick={() => {
                onConfirm?.();
                onOpenChange(false);
              }}
            >
              {confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Keyframes for the dialog (kept here so the component is self-contained) ─
// Tailwind @theme already loads our utility keyframes; we add these two
// imperatively because the dialog uses data-[state=open] which Radix toggles.
if (typeof document !== "undefined" && !document.getElementById("confirm-dialog-keyframes")) {
  const style = document.createElement("style");
  style.id = "confirm-dialog-keyframes";
  style.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes scaleIn {
      from { opacity: 0; transform: translate(-50%, -48%) scale(0.97); }
      to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    }
  `;
  document.head.appendChild(style);
}
